import React, { useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Space, Tooltip } from 'antd';
import { CopyOutlined, EyeInvisibleOutlined, EyeTwoTone, PlusOutlined } from '@ant-design/icons';
import { ListBase, useDataProvider, useListContext, useNotify } from 'react-admin';
import { TableListColumn, NetOrcaTableListV1 } from '@src/components/Common/NetOrcaTableList';
import DeleteButton from '@src/components/Common/DeleteButton';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';

/** One key under the shared column header. */
const ApiKeyRow = ({ record, refetch }: { record: any; refetch: () => void }) => (
    <div className="tm-row">
        <Tooltip placement="topLeft" title={record.name}>
            <div className="tm-cell tm-cell--strong" data-label="Name">
                {record.name}
            </div>
        </Tooltip>

        <Tooltip title={record.created ? formatDateAndTime(record.created) : undefined}>
            <div className="tm-cell tm-cell--muted" data-label="Created">
                {record.created ? formatDateAndTimeFromNow(record.created) : '—'}
            </div>
        </Tooltip>

        <div className="tm-actions">
            <DeleteButton title="API key" resource="apikeys" record={record} size="small" refetch={refetch} />
        </div>
    </div>
);

const ApiKeysView = () => {
    const { refetch } = useListContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [form] = Form.useForm();
    const [creating, setCreating] = useState(false);
    const [saving, setSaving] = useState(false);

    const copy = (key: string) => {
        navigator.clipboard.writeText(key);
        notify('Copied to clipboard');
    };

    /**
     * The one and only sight of the key itself. The API returns it on creation
     * and never again, so it is shown behind a reveal with a copy beside it
     * rather than printed into the page.
     */
    const showKey = (key: string) =>
        Modal.info({
            closable: true,
            width: 560,
            maskClosable: true,
            title: 'Your new API key',
            content: (
                <div>
                    Save this key somewhere safe — it will not be shown again.
                    <Space.Compact style={{ width: '100%', marginTop: 10 }}>
                        <Input.Password
                            readOnly
                            value={key}
                            style={{ flex: 1 }}
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                        <Button icon={<CopyOutlined />} onClick={() => copy(key)} />
                    </Space.Compact>
                </div>
            ),
        });

    const create = (values: any) => {
        setSaving(true);
        // `idField` is this data provider's own: the create response carries the
        // key itself, and it says which of its fields is the record's id
        const params: any = { data: values, idField: 'id' };
        dataProvider
            .create('apikeys', params)
            .then((response: any) => {
                notify('API key created');
                setCreating(false);
                form.resetFields();
                refetch();
                showKey(response.data?.key);
            })
            .catch((error: any) => {
                if (error.status === 400 && error.body) {
                    Object.entries<any>(error.body).forEach(([name, errors]) => form.setFields([{ name, errors }]));
                    if (error.body.non_field_errors) notify('Could not create the API key', 'error');
                } else {
                    notify('Something went wrong', 'warning');
                }
            })
            .finally(() => setSaving(false));
    };

    // Nothing on a key is worth narrowing by — a team has a handful of them and
    // the header is here to name the columns, not to filter them.
    const columns: TableListColumn[] = useMemo(
        () => [
            { label: 'Name', dataCy: 'column-name' },
            { label: 'Created', dataCy: 'column-created' },
            // the last track is the create button's, over each row's delete
        ],
        [],
    );

    return (
        <>
            <NetOrcaTableListV1
                className="tm-list tm-list--keys"
                dataCy="api-key-list"
                fields={[]}
                columns={columns}
                template="var(--tm-key-cols)"
                emptyText="This team has no API keys yet"
                noun="API key"
                plural="API keys"
                headerTrailing={
                    <Button
                        className="tm-head-action"
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => setCreating(true)}
                    >
                        New key
                    </Button>
                }
                renderRow={(record) => <ApiKeyRow key={record.id} record={record} refetch={refetch} />}
            />

            <Modal
                title="New API key"
                open={creating}
                onCancel={() => setCreating(false)}
                confirmLoading={saving}
                okText="Create"
                onOk={form.submit}
            >
                <Form form={form} name="api_key" layout="vertical" onFinish={create}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Give the key a name' }]}
                    >
                        <Input placeholder="CI pipeline" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

/**
 * The keys that speak for the team. The header names the columns but filters
 * nothing — there is no question to ask of a list this short — and the control
 * that writes a new one closes that same header rather than standing under the
 * list.
 */
const ApiKeysTab = () => (
    <div className="tm-tab">
        <ListBase resource="apikeys" basePath="/teams" sort={{ field: 'created', order: 'DESC' }} perPage={25}>
            <ApiKeysView />
        </ListBase>
    </div>
);

export default ApiKeysTab;
